import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Poll, PollOption } from '@/types/group';

export const usePollData = (tripId: string, showClosed: boolean) => {
  // Keep track of deleted poll IDs locally
  const [deletedPollIds, setDeletedPollIds] = useState<string[]>([]);
  
  // Fetch polls
  const { 
    data: polls, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['polls', tripId, showClosed, deletedPollIds],
    queryFn: async () => {
      const query = supabase
        .from('polls')
        .select(`
          *,
          poll_options:poll_options(
            *,
            votes:poll_votes(*)
          )
        `)
        .eq('trip_id', tripId);
      
      if (!showClosed) {
        query.eq('closed', false);
      }
      
      // Don't include locally marked deleted polls
      if (deletedPollIds.length > 0) {
        query.not('id', 'in', `(${deletedPollIds.join(',')})`);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Process data to count votes for each option
      return data.map((poll: any): Poll => {
        const options = poll.poll_options.map((option: any): PollOption => {
          return {
            ...option,
            vote_count: option.votes ? option.votes.length : 0
          };
        });
        
        return {
          ...poll,
          options
        };
      });
    }
  });

  // Setup realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('poll-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'polls'
      }, (payload) => {
        // If a poll is deleted, add it to our local deleted polls list
        if (payload.eventType === 'DELETE') {
          const deletedId = payload.old?.id;
          if (deletedId) {
            setDeletedPollIds(prev => [...prev, deletedId]);
          }
        }
        refetch();
      })
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'poll_options'
      }, () => {
        refetch();
      })
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'poll_votes'
      }, () => {
        refetch();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Function to mark a poll as deleted locally
  const markPollAsDeleted = (pollId: string) => {
    setDeletedPollIds(prev => [...prev, pollId]);
  };

  return {
    polls,
    isLoading,
    error,
    refetch,
    markPollAsDeleted
  };
};
